import { useEffect, useState } from "react";
import axios from "axios";
import { BASE_URL } from "../../../../config/api";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import SubscribedTab from "./SubscribedTab";
import RegularLoader from "../../../../Component/Loader/RegularLoader";
import toast from "react-hot-toast";


/* ── Check Icon ─────────────────────────────────────────────────────────── */
const CheckIcon = ({ featured }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle
      cx="8" cy="8" r="7.5"
      fill={featured ? "rgba(255,255,255,0.15)" : "rgba(59,130,246,0.1)"}
      stroke={featured ? "rgba(255,255,255,0.3)" : "rgba(59,130,246,0.3)"}
    />
    <path
      d="M5 8.5l2 2 4-4"
      stroke={featured ? "#93c5fd" : "#3b82f6"}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ShieldIcon = ({ featured }) => (
  <svg width="14" height="16" viewBox="0 0 22 26" fill="none">
    <path
      d="M11 1L2 5v8c0 6 4 11 9 13 5-2 9-7 9-13V5L11 1z"
      stroke={featured ? "rgba(255,255,255,0.4)" : "#94a3b8"}
      strokeWidth="1.5"
      fill="none"
    />
    <path
      d="M7 13l2.5 2.5L15 10"
      stroke={featured ? "rgba(255,255,255,0.4)" : "#94a3b8"}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* ── Small shield icon ─────────────────────────────────────────────────── */
// const ShieldIcon = () => (
//   <svg width="13" height="15" viewBox="0 0 22 26" fill="none">
//     <path d="M11 1L2 5v8c0 6 4 11 9 13 5-2 9-7 9-13V5L11 1z"
//       stroke="#94a3b8" strokeWidth="1.5" fill="none" />
//     <path d="M7 13l2.5 2.5L15 10"
//       stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
//   </svg>
// );

/* ── Category pill color map ───────────────────────────────────────────── */
function getCategoryStyle(name = "") {
  const n = name.toLowerCase();
  if (n.includes("silver"))   return { bg: "#e2e8f0", color: "#475569" };
  if (n.includes("gold"))     return { bg: "#fef3c7", color: "#92400e" };
  if (n.includes("platinum")) return { bg: "#ede9fe", color: "#5b21b6" };
  if (n.includes("diamond"))  return { bg: "#fce7f3", color: "#9d174d" };
  return { bg: "#dbeafe", color: "#1e40af" };
}

/* ── ProductCard ─────────────────────────────────────────────────────────── */
function ProductCard({ product, index, isPopular, navigate }) {
  const isFeatured = isPopular;
  const isSubscribed = Boolean(product?.is_subscribed);
  const benefits = product.retailer_benifits
    ? product.retailer_benifits.split(".").map((b) => b.trim()).filter(Boolean)
    : [];
  const [showAllBenefits, setShowAllBenefits] = useState(false);


  const handleSubscribe = async () => {
    if (isSubscribed) return;
    try {
      const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");
      const res = await axios.get(`${BASE_URL}warranty/check-retailer-active-plan`, {
        params: { retailer_id: authUser?.id, package_id: product?.id },
      });
      const data = res.data;
      if (data?.success && data?.has_active_plan) {
        toast.error("You are already subscribed to this product. Please select a different product.");
        return;
      }
      if (data?.success && !data?.has_active_plan) {
        navigate("/ordersummery", { state: { company_product_id: product.company_product_id } });
      }
    } catch (err) {
      console.error(err);
      alert("Something went wrong");
    }
  };

  /* Pricing math */
  const BASE_PRICE = 299;
  const offerPerDevice = product?.enroll_max > 0
    ? Math.round(product.mrp / product.enroll_max)
    : Number(product.mrp);
  const planTotalCost = Number(product.mrp).toLocaleString("en-IN");
  const saving = BASE_PRICE - offerPerDevice;

  /* Category name for the pill badge */
  const categoryName = product.categories?.[0]?.name || "";
  const pillStyle = getCategoryStyle(categoryName);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.08, ease: "easeOut" }}
      className="relative w-full"
      style={{ paddingTop: isFeatured ? "20px" : "0" }}
    >

      {/* ── Most Popular floating badge ── */}
      {isFeatured && (
        <div className="absolute -top-0.5 left-0 right-0 flex justify-center z-20">
          <motion.div
            animate={{ y: [0, -3, 0] }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="relative overflow-hidden"
          >
            <div
              className="relative overflow-hidden flex items-center gap-2 px-4 py-2"
              style={{
                background: "linear-gradient(90deg, #f97316, #fb923c)",
                borderRadius: "999px",
                boxShadow:
                  "0 8px 24px rgba(249,115,22,0.45), 0 0 20px rgba(251,146,60,0.35)",
              }}
            >
              {/* Shine */}
              <motion.div
                className="absolute top-0 h-full w-10"
                animate={{
                  x: ["-150%", "400%"],
                }}
                transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  ease: "linear",
                }}
                style={{
                  background:
                    "linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent)",
                  transform: "skewX(-20deg)",
                }}
              />

              <span className="relative z-10 text-white text-[11px] font-black tracking-wider">
                ★ MOST POPULAR ★
              </span>
            </div>
          </motion.div>
        </div>
      )}

      {/* ── Card ── */}
      <motion.div
        whileHover={{
          y: -6,
          scale: 1.01,
        }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 20,
        }}
        className="flex flex-col transition-all duration-300 hover:-translate-y-0.5"
        style={{
          borderRadius: "16px",
          background: isFeatured
            ? "linear-gradient(145deg, #2563eb 0%, #1d4ed8 50%, #1e40af 100%)"
            : "#ffffff",
          border: isFeatured
            ? "1px solid rgba(255,255,255,0.15)"
            : "1px solid #e2e8f0",
          boxShadow: isFeatured
            ? "0 20px 60px rgba(37,99,235,0.4), 0 4px 16px rgba(0,0,0,0.15)"
            : "0 2px 12px rgba(15,23,42,0.06), 0 1px 4px rgba(0,0,0,0.04)",
          overflow: "hidden",
        }}
      >
        <div className="px-3 sm:px-5 pt-3 sm:pt-5 pb-2 sm:pb-5 flex flex-col gap-0">

          {/* ── Title ── */}
          <h3
            className="text-[16px] sm:text-[16px] font-black leading-tight mb-2"
            style={{ color: isFeatured ? "#ffffff" : "#1e40af" }}
          >
            {product.name}
          </h3>

          {/* ── Category Pill ── */}
         <div className="flex gap-2">
           {categoryName && (
            <div className="mb-2">
              <span
                className="text-[10px] sm:text-[9px] font-bold px-2 sm:px-3 py-1 rounded-full"
                style={{
                  background: isFeatured ? "rgba(255,255,255,0.18)" : pillStyle.bg,
                  color: isFeatured ? "rgba(255,255,255,0.9)" : pillStyle.color,
                }}
              >
                ({categoryName})
              </span>
            </div>
          )}

          
         </div>
          {/* ── Base price (strikethrough) + offer per device ── */}
          <div className="flex items-center gap-2 mb-1">
            <span
              className="text-[14px] sm:text-[11px] font-semibold line-through"
              style={{
                color: isFeatured ? "rgba(255,255,255,0.45)" : "#94a3b8",
                textDecorationColor: isFeatured ? "rgba(255,255,255,0.45)" : "#94a3b8",
              }}
            >
              ₹{BASE_PRICE}
            </span>
            <span
              className="text-[14px] sm:text-[12px] font-black"
              style={{ color: isFeatured ? "#93c5fd" : "#1d4ed8" }}
            >
              ₹{offerPerDevice} per Device
            </span>
          </div>

          {/* ── Plan Total Cost (big number) ── */}
          <div className="flex items-baseline gap-2 mb-3">
            <motion.span
                className="leading-none text-[26px] sm:text-[30px] font-semibold"
                style={{color: isFeatured ? "#ffffff" : "#0f172a",}}
                animate={
                  isFeatured
                    ? {
                        scale: [1, 1.03, 1],
                      }
                    : {}
                }
                transition={{
                  duration: 2,
                  repeat: Infinity,
                }}
              >
              ₹{planTotalCost}
            </motion.span>
            <span
              className="text-[11px] font-semibold"
              style={{ color: isFeatured ? "rgba(255,255,255,0.55)" : "#94a3b8" }}
            >
              Plan Total Cost
            </span>
          </div>

          {/* ── Save badge ── */}
          {saving > 0 && (
            <motion.span
              className="inline-flex items-center gap-1 text-[10px] font-bold"
              animate={{
                y: [0, -3, 0],
                scale: [1, 1.05, 1],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              style={{
                color: isFeatured ? "#bfdbfe" : "#f97316",
              }}
            >
              🔥 Save ₹{saving} Per Device
            </motion.span>
          )}

          <div className="mt-2 mb-3 flex justify-start">
          <motion.div
              animate={{
                boxShadow: [
                  "0 0 0 rgba(16,185,129,0)",
                  "0 0 12px rgba(16,185,129,0.35)",
                  "0 0 0 rgba(16,185,129,0)",
                ],
              }}
              transition={{
                duration: 2.5,
                repeat: Infinity,
              }}
            className={`
              relative inline-flex items-center gap-2
              px-2 sm:px-4 py-1.5 sm:py-2 rounded-full
              overflow-hidden
              shadow-lg
              border
              backdrop-blur-md
              transition-all duration-300
              hover:scale-[1.03]
              hover:shadow-xl

              ${
                isFeatured
                  ? "border-white/20"
                  : "border-emerald-200"
              }
            `}
            style={{
              background: isFeatured
                ? "linear-gradient(135deg, rgba(255,255,255,0.16), rgba(255,255,255,0.08))"
                : "linear-gradient(135deg, #ecfdf5, #d1fae5)",
            }}
          >
            {/* Glow */}
            <div
              className="absolute inset-0 opacity-40"
              style={{
                background: isFeatured
                  ? "radial-gradient(circle at top left, rgba(255,255,255,0.25), transparent 60%)"
                  : "radial-gradient(circle at top left, rgba(16,185,129,0.18), transparent 60%)",
              }}
            />

            {/* Icon */}
            <div
              className={`
                relative z-10
                w-4 h-4 sm:w-6 sm:h-6 rounded-full
                flex items-center justify-center
                text-[10px] font-black

                ${
                  isFeatured
                    ? "bg-white/20 text-yellow-300"
                    : "bg-emerald-500 text-white"
                }
              `}
            >
              ✓
            </div>

            {/* Text */}
            <span
              className={`
                relative z-10
                text-[7px] sm:text-[8.5px]
                font-black
                uppercase
                tracking-[0.12em]

                ${
                  isFeatured
                    ? "text-white"
                    : "text-emerald-700"
                }
              `}
            >
              12 Month Extended Warranty
            </span>

            {/* Shine */}
            <div
              className="
                absolute top-0 -left-10 w-8 h-full
                bg-white/20 rotate-12
                blur-md
              "
            />
          </motion.div>
        </div>

          {/* ── Divider ── */}
          <div
            className="mb-2"
            style={{
              height: "1px",
              background: isFeatured ? "rgba(255,255,255,0.12)" : "#f1f5f9",
            }}
          />

          {/* ── Valid For row ── */}
          {/* <div className="flex items-center justify-between mb-2">
            <span
              className="text-[13px] font-medium"
              style={{ color: isFeatured ? "rgba(255,255,255,0.6)" : "#64748b" }}
            >
              Valid For
            </span>
            <span
              className="text-[13px] font-black"
              style={{ color: isFeatured ? "#93c5fd" : "#2563eb" }}
            >
              {product.sub_val_days} Days
            </span>
          </div> */}

          {/* ── Enroll Upto row ── */}
           <motion.div
            whileHover={{
              scale: 1.02,
            }}
            transition={{
              duration: 0.2,
            }}
            className="flex rounded-2xl overflow-hidden mb-3"
            style={{
              background: isFeatured ? "rgba(255,255,255,0.08)" : "#f8fafc",
              border: isFeatured ? "1px solid rgba(255,255,255,0.12)" : "1px solid #e2e8f0",
            }}
          >
            <div className="flex-1 py-2 sm:py-4 text-center">
              <p
                className="text-[18px] sm:text-[22px] font-black leading-none mb-1"
                style={{ color: isFeatured ? "#ffffff" : "#0f172a" }}
              >
                {product.enroll_max}
              </p>
              <p
                className="text-[8px] sm:text-[11px] font-semibold uppercase tracking-wider"
                style={{ color: isFeatured ? "rgba(255,255,255,0.45)" : "#94a3b8" }}
              >
                Devices Covered
              </p>
            </div>
            <div
              className="w-px"
              style={{ background: isFeatured ? "rgba(255,255,255,0.12)" : "#e2e8f0" }}
            />
            <div className="flex-1 py-2 sm:py-4 text-center">
              <p
                className="text-[18px] sm:text-[22px] font-black leading-none mb-1"
                style={{ color: isFeatured ? "#ffffff" : "#0f172a" }}
              >
                {product.sub_val_days}
              </p>
              <p
                className="text-[8px] sm:text-[11px] font-semibold uppercase tracking-wider"
                style={{ color: isFeatured ? "rgba(255,255,255,0.45)" : "#94a3b8" }}
              >
                Days Validity
              </p>
            </div>
          </motion.div>

          {benefits.length > 0 && (
            <div className="mb-5">
              <p
                className="text-[8px] font-black uppercase tracking-[0.2em] mb-3"
                style={{
                  color: isFeatured
                    ? "rgba(255,255,255,0.45)"
                    : "#94a3b8",
                }}
              >
                Retailer Benefits
              </p>

              <ul className="space-y-2.5">
                {(showAllBenefits
                  ? benefits
                  : benefits.slice(0, 2)
                ).map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckIcon featured={isFeatured} />

                    <span
                      className="text-[10px] sm:text-[12px] font-medium leading-snug"
                      style={{
                        color: isFeatured
                          ? "rgba(255,255,255,0.85)"
                          : "#334155",
                      }}
                    >
                      {b}
                    </span>
                  </li>
                ))}
              </ul> 

              {/* SHOW MORE BUTTON */}
                {benefits.length > 2 && (
                  <button
                    onClick={() =>
                      setShowAllBenefits(!showAllBenefits)
                    }
                    className="mt-3 text-[11px] font-bold transition-all"
                    style={{
                      color: isFeatured
                        ? "#93c5fd"
                        : "#2563eb",
                    }}
                  >
                    {showAllBenefits
                      ? "Show Less −"
                      : `Show ${benefits.length - 2} More +`}
                  </button>
                )}
              </div>
            )}

          {/* ── Subscribe Button ── */}
          <button
            onClick={handleSubscribe}
            className="w-full py-2 sm:py-3 rounded-xl font-black text-[10px] sm:text-[12px] tracking-wide transition-all duration-200 active:scale-[0.97]"
            style={{
              background: isSubscribed
                ? (isFeatured ? "rgba(255,255,255,0.15)" : "#f0fdf4")
                : isFeatured
                ? "rgba(255,255,255,0.95)"
                : "linear-gradient(90deg, #2563eb, #1d4ed8)",
              color: isSubscribed
                ? (isFeatured ? "#86efac" : "#16a34a")
                : isFeatured
                ? "#1e40af"
                : "#ffffff",
              border: isSubscribed
                ? (isFeatured ? "1px solid rgba(255,255,255,0.2)" : "1px solid #bbf7d0")
                : "none",
              boxShadow: isSubscribed
                ? "none"
                : isFeatured
                ? "0 4px 16px rgba(0,0,0,0.2)"
                : "0 4px 16px rgba(37,99,235,0.3)",
              cursor: isSubscribed ? "default" : "pointer",
              letterSpacing: "0.03em",
            }}
          >
            {isSubscribed ? "✓  Subscribed" : "Subscribe Now"}
          </button>

          {/* ── Footer ── */}
          <div
            className="flex items-center justify-center gap-1.5 mt-2 text-[11px] font-medium"
            style={{ color: isFeatured ? "rgba(255,255,255,0.35)" : "#94a3b8" }}
          >
            <ShieldIcon />
            Secure subscription plan
          </div>

        </div>
      </motion.div>
    </motion.div>
  );
}

export default function RetailerProductView() {
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);

  const [isOrderOpen, setIsOrderOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const authUserString = localStorage.getItem("authUserData");
  const authUser = authUserString ? JSON.parse(authUserString) : null;
  const companyId = authUser?.company_id;
  const retailerid = authUser?.id;

   const location = useLocation();

  const [activeTab, setActiveTab] = useState(
    location.state?.activeTab ||
    (Number(authUser?.is_subscribed) === 1
      ? "subscribed"
      : "subscription")
  );
  const [subscribedProducts, setSubscribedProducts] = useState([]);

  const navigate = useNavigate()

  const isRetailerMobile =
  typeof window !== "undefined" &&
  window.innerWidth < 640 &&
  (authUser?.role === 5 || authUser?.role === 6);
 

   const mostPopularId = products.length
  ? products.reduce((max, p) =>
      Number(p.sold_count) > Number(max.sold_count) ? p : max
    ).id
  : null;

useEffect(() => {
  if (location.state?.activeTab) {
    setActiveTab(location.state.activeTab);
  } else {
    setActiveTab(
      Number(authUser?.is_subscribed) === 1
        ? "subscribed"
        : "subscription"
    );
  }
}, [location.state, authUser?.is_subscribed]);

  useEffect(() => {
    async function fetchProducts() {
      if (!companyId) return;
      setLoading(true);
      try {
        const res = await axios.get(`${BASE_URL}warranty/company-products`, {
          params: { company_id: companyId , is_offer: 1, retailer_id: retailerid },
        });
        const companyBlock = res.data?.data?.[0];
        const mapped =
          companyBlock?.products?.map((cp) => ({
            company_product_id: cp.company_product_id,
            margin: cp.margin,

            // ✅ ADD THIS
            is_subscribed: cp.is_subscribed,

            // ✅ OPTIONAL
            subscription: cp.subscription,

            ...cp.product,
          })) || [];
        setProducts(mapped);
      } catch (e) {
        console.error("Company products error", e);
      } finally {
        setLoading(false);
      }
    }
    fetchProducts();
  }, [companyId]);


  if (loading) {
    return (
      <RegularLoader/>
    );
  }

  if (!products.length) {
    return (
      <div className="flex items-center justify-center py-5 text-slate-400 text-sm">
        No products available.
      </div>
    );
  }

  return (
    /* py-16 gives breathing room for the featured card's translateY(-28px) to protrude into */
    
    <div
      className={`px-6 py-2 min-h-screen ${
        isRetailerMobile
          ? ""
          : ""
      }`}
    >

      <div className="flex gap-3 mb-6">
  {Number(authUser?.is_subscribed) === 1 ? (
    <>
      <button
        onClick={() => setActiveTab("subscribed")}
        className={`px-4 py-1 rounded-xl text-sm font-semibold transition ${
          activeTab === "subscribed"
            ? "bg-blue-600 text-white shadow"
            : "bg-white text-slate-600 border"
        }`}
      >
        Subscribed Products
      </button>

      <button
        onClick={() => setActiveTab("subscription")}
        className={`px-4 py-1 rounded-xl text-sm font-semibold transition ${
          activeTab === "subscription"
            ? "bg-blue-600 text-white shadow"
            : "bg-white text-slate-600 border"
        }`}
      >
        Subscription
      </button>
    </>
  ) : (
    <>
      <button
        onClick={() => setActiveTab("subscription")}
        className={`px-4 py-1 rounded-xl text-sm font-semibold transition ${
          activeTab === "subscription"
            ? "bg-blue-600 text-white shadow"
            : "bg-white text-slate-600 border"
        }`}
      >
        Subscription
      </button>

      <button
        onClick={() => setActiveTab("subscribed")}
        className={`px-4 py-1 rounded-xl text-sm font-semibold transition ${
          activeTab === "subscribed"
            ? "bg-blue-600 text-white shadow"
            : "bg-white text-slate-600 border"
        }`}
      >
        Subscribed Products
      </button>
    </>
  )}
</div>
     {/* 👇 CONDITIONAL RENDER */}
    {activeTab === "subscription" && (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {products.map((p, i) => (
          <div key={p.company_product_id} className="w-full">
            <ProductCard
              product={p}
              index={i}
              isPopular={p.id === mostPopularId}
              navigate={navigate}
              products={products}
            />
          </div>
        ))}
      </div>
    )}

  {activeTab === "subscribed" && (
  <SubscribedTab companyId={retailerid} />
)}

    </div>
  );
}

// import { useEffect, useState } from "react";
// import axios from "axios";
// import { BASE_URL } from "../../../../config/api";
// import { motion } from "framer-motion";
// import { useNavigate } from "react-router-dom";
// import { useLocation } from "react-router-dom";
// import SubscribedTab from "./SubscribedTab";
// import RegularLoader from "../../../../Component/Loader/RegularLoader";
// import toast from "react-hot-toast";


// /* ── Check Icon ─────────────────────────────────────────────────────────── */
// const CheckIcon = ({ featured }) => (
//   <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
//     <circle
//       cx="8" cy="8" r="7.5"
//       fill={featured ? "rgba(255,255,255,0.15)" : "rgba(59,130,246,0.1)"}
//       stroke={featured ? "rgba(255,255,255,0.3)" : "rgba(59,130,246,0.3)"}
//     />
//     <path
//       d="M5 8.5l2 2 4-4"
//       stroke={featured ? "#93c5fd" : "#3b82f6"}
//       strokeWidth="1.5"
//       strokeLinecap="round"
//       strokeLinejoin="round"
//     />
//   </svg>
// );

// const ShieldIcon = ({ featured }) => (
//   <svg width="14" height="16" viewBox="0 0 22 26" fill="none">
//     <path
//       d="M11 1L2 5v8c0 6 4 11 9 13 5-2 9-7 9-13V5L11 1z"
//       stroke={featured ? "rgba(255,255,255,0.4)" : "#94a3b8"}
//       strokeWidth="1.5"
//       fill="none"
//     />
//     <path
//       d="M7 13l2.5 2.5L15 10"
//       stroke={featured ? "rgba(255,255,255,0.4)" : "#94a3b8"}
//       strokeWidth="1.5"
//       strokeLinecap="round"
//       strokeLinejoin="round"
//     />
//   </svg>
// );

// /* ── ProductCard ─────────────────────────────────────────────────────────── */
// function ProductCard({ product, index, isPopular, navigate }) {
//   const isFeatured = isPopular;
//   const [showAllBenefits, setShowAllBenefits] = useState(false);

//   const handleSubscribe = async () => {
//     if (product?.is_subscribed) return;
//     try {
//       const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");
//       const res = await axios.get(`${BASE_URL}warranty/check-retailer-active-plan`, {
//         params: { retailer_id: authUser?.id, package_id: product?.id },
//       });
//       const data = res.data;
//       if (data?.success && data?.has_active_plan) {
//         toast.error("You are already subscribed to this product. Please select a different product.");
//         return;
//       }
//       if (data?.success && !data?.has_active_plan) {
//         navigate("/ordersummery", { state: { company_product_id: product.company_product_id } });
//       }
//     } catch (err) {
//       console.error(err);
//       alert("Something went wrong");
//     }
//   };

//   const isSubscribed = Boolean(product?.is_subscribed);

//   const basePrice = 299;
//   const offerPrice = product?.enroll_max > 0
//     ? Math.round(product.mrp / product.enroll_max)
//     : product.mrp;
//   const savingAmount = basePrice - offerPrice;
//   const savingPercent = Math.round((savingAmount / basePrice) * 100);

//   const features = product.features
//     ? product.features.split("✔").map((f) => f.trim()).filter(Boolean)
//     : [];

//   const benefits = product.retailer_benifits
//     ? product.retailer_benifits.split(".").map((b) => b.trim()).filter(Boolean)
//     : [];

//   const coverageText = product.coverage
//     ? product.coverage.split(".")[0].trim()
//     : "";

//   return (
//     <motion.div
//       initial={{ opacity: 0, y: 30 }}
//       animate={{ opacity: 1, y: 0 }}
//       transition={{ duration: 0.45, delay: index * 0.1, ease: "easeOut" }}
//       className="relative w-full max-w-[380px] mx-auto"
//       style={{ paddingTop: isFeatured ? "22px" : "0" }}
//     >
//       {/* ── Most Popular Badge (floats above card) ── */}
//       {isFeatured && (
//         <div className="absolute top-0 left-1/2 -translate-x-1/2 z-20">
//           <div
//             className="flex items-center gap-1.5 px-5 py-2 text-white text-[12px] font-black uppercase tracking-widest whitespace-nowrap"
//             style={{
//               background: "linear-gradient(90deg, #f97316, #fb923c)",
//               borderRadius: "999px",
//               boxShadow: "0 6px 20px rgba(249,115,22,0.5)",
//               letterSpacing: "0.12em",
//             }}
//           >
//             ★ &nbsp;Most Popular&nbsp; ★
//           </div>
//         </div>
//       )}

//       {/* ── Card Shell ── */}
//       <div
//         className="relative overflow-hidden flex flex-col transition-all duration-300 hover:-translate-y-1"
//         style={{
//           borderRadius: "24px",
//           background: isFeatured
//             ? "linear-gradient(160deg, #2d4fd6 0%, #1e3a8a 45%, #172554 100%)"
//             : "#ffffff",
//           border: isFeatured
//             ? "1px solid rgba(255,255,255,0.12)"
//             : "1px solid #e2e8f0",
//           boxShadow: isFeatured
//             ? "0 30px 80px rgba(37,99,235,0.45), 0 8px 24px rgba(0,0,0,0.2)"
//             : "0 10px 40px rgba(15,23,42,0.08), 0 2px 8px rgba(0,0,0,0.04)",
//         }}
//       >
//         {/* Radial glow overlay */}
//         <div
//           className="absolute inset-0 pointer-events-none"
//           style={{
//             background: isFeatured
//               ? "radial-gradient(circle at 30% 0%, rgba(255,255,255,0.12) 0%, transparent 55%)"
//               : "radial-gradient(circle at 30% 0%, rgba(59,130,246,0.05) 0%, transparent 55%)",
//           }}
//         />

//         {/* ── Content ── */}
//         <div className="relative z-10 flex flex-col px-7 pt-8 pb-7">

//           {/* Title + Subtitle */}
//           <div className="mb-5">
//             <h3
//               className="text-[20px] font-black leading-tight mb-1"
//               style={{ color: isFeatured ? "#ffffff" : "#0f172a" }}
//             >
//               {product.name}
//             </h3>
//             {/* {coverageText && (
//               <p
//                 className="text-[14px] font-medium leading-snug"
//                 style={{ color: isFeatured ? "rgba(255,255,255,0.6)" : "#64748b" }}
//               >
//                 {coverageText}
//               </p>
//             )} */}
//           </div>

//           {/* ── Pricing ── */}
//           <div className="mb-5">
//             {/* Base price struck through */}
//             <p
//               className="text-[15px] font-semibold line-through mb-1"
//               style={{
//                 color: isFeatured ? "rgba(255,255,255,0.4)" : "#94a3b8",
//                 textDecorationColor: isFeatured ? "rgba(255,255,255,0.4)" : "#94a3b8",
//               }}
//             >
//               ₹{basePrice} Per Device
//             </p>

//             {/* Offer price big */}
//             <div className="flex items-baseline gap-2 mb-1">
//               <span
//                 className="font-black leading-none"
//                 style={{
//                   fontSize: "40px",
//                   color: isFeatured ? "#ffffff" : "#0f172a",
//                   letterSpacing: "-0.03em",
//                 }}
//               >
//                 ₹{product.mrp}
//               </span>
//               <span
//                 className="text-[14px] font-semibold"
//                 style={{ color: isFeatured ? "rgba(255,255,255,0.6)" : "#64748b" }}
//               >
//                Only
//               </span>
//             </div>

//             {/* Per device */}
//             <p
//               className="text-[13px] font-medium mb-4"
//               style={{ color: isFeatured ? "rgba(255,255,255,0.55)" : "#94a3b8" }}
//             >
//               ₹{offerPrice} Per Device
//             </p>

//             {/* Save badge */}
//             <div className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[12px] font-bold"
//               style={{
//                 background: isFeatured ? "rgba(255,255,255,0.12)" : "#f0f9ff",
//                 border: isFeatured ? "1px solid rgba(255,255,255,0.2)" : "1px solid #bae6fd",
//                 color: isFeatured ? "#93c5fd" : "#0369a1",
//               }}
//             >
//               🎉 Save {savingPercent}% · Best Value
//             </div>
//           </div>

//           {/* Divider */}
//           <div
//             className="mb-5"
//             style={{
//               height: "1px",
//               background: isFeatured
//                 ? "rgba(255,255,255,0.12)"
//                 : "#f1f5f9",
//             }}
//           />

//           {/* ── Retailer Benefits ── */}


//           {/* ── Devices + Days Stats Row ── */}
//           <div
//             className="flex rounded-2xl overflow-hidden mb-6"
//             style={{
//               background: isFeatured ? "rgba(255,255,255,0.08)" : "#f8fafc",
//               border: isFeatured ? "1px solid rgba(255,255,255,0.12)" : "1px solid #e2e8f0",
//             }}
//           >
//             <div className="flex-1 py-4 text-center">
//               <p
//                 className="text-[28px] font-black leading-none mb-1"
//                 style={{ color: isFeatured ? "#ffffff" : "#0f172a" }}
//               >
//                 {product.enroll_max}
//               </p>
//               <p
//                 className="text-[11px] font-semibold uppercase tracking-wider"
//                 style={{ color: isFeatured ? "rgba(255,255,255,0.45)" : "#94a3b8" }}
//               >
//                 Devices Covered
//               </p>
//             </div>
//             <div
//               className="w-px"
//               style={{ background: isFeatured ? "rgba(255,255,255,0.12)" : "#e2e8f0" }}
//             />
//             <div className="flex-1 py-4 text-center">
//               <p
//                 className="text-[28px] font-black leading-none mb-1"
//                 style={{ color: isFeatured ? "#ffffff" : "#0f172a" }}
//               >
//                 {product.sub_val_days}
//               </p>
//               <p
//                 className="text-[11px] font-semibold uppercase tracking-wider"
//                 style={{ color: isFeatured ? "rgba(255,255,255,0.45)" : "#94a3b8" }}
//               >
//                 Days Validity
//               </p>
//             </div>
//           </div>

//                     {benefits.length > 0 && (
//             <div className="mb-5">
//               <p
//                 className="text-[10px] font-black uppercase tracking-[0.2em] mb-3"
//                 style={{
//                   color: isFeatured
//                     ? "rgba(255,255,255,0.45)"
//                     : "#94a3b8",
//                 }}
//               >
//                 Retailer Benefits
//               </p>

//               <ul className="space-y-2.5">
//                 {(showAllBenefits
//                   ? benefits
//                   : benefits.slice(0, 2)
//                 ).map((b, i) => (
//                   <li key={i} className="flex items-start gap-3">
//                     <CheckIcon featured={isFeatured} />

//                     <span
//                       className="text-[14px] font-medium leading-snug"
//                       style={{
//                         color: isFeatured
//                           ? "rgba(255,255,255,0.85)"
//                           : "#334155",
//                       }}
//                     >
//                       {b}
//                     </span>
//                   </li>
//                 ))}
//               </ul>

//               {/* SHOW MORE BUTTON */}
//                       {benefits.length > 2 && (
//                         <button
//                           onClick={() =>
//                             setShowAllBenefits(!showAllBenefits)
//                           }
//                           className="mt-3 text-[13px] font-bold transition-all"
//                           style={{
//                             color: isFeatured
//                               ? "#93c5fd"
//                               : "#2563eb",
//                           }}
//                         >
//                           {showAllBenefits
//                             ? "Show Less −"
//                             : `Show ${benefits.length - 2} More +`}
//                         </button>
//                       )}
//                     </div>
//                   )}

//           {/* ── CTA Button ── */}
//           <button
//             onClick={handleSubscribe}
//             className="w-full py-4 rounded-2xl font-black text-[15px] tracking-wide transition-all duration-200 active:scale-[0.97] hover:scale-[1.01]"
//             style={{
//               background: isSubscribed
//                 ? (isFeatured ? "rgba(255,255,255,0.15)" : "#f0fdf4")
//                 : isFeatured
//                 ? "#ffffff"
//                 : "linear-gradient(90deg, #2563eb, #3b5bdb)",
//               color: isSubscribed
//                 ? (isFeatured ? "#86efac" : "#16a34a")
//                 : isFeatured
//                 ? "#1e3a8a"
//                 : "#ffffff",
//               border: isSubscribed
//                 ? (isFeatured ? "1px solid rgba(255,255,255,0.2)" : "1px solid #bbf7d0")
//                 : "none",
//               boxShadow: isSubscribed
//                 ? "none"
//                 : isFeatured
//                 ? "0 8px 24px rgba(0,0,0,0.2)"
//                 : "0 8px 24px rgba(37,99,235,0.35)",
//               cursor: isSubscribed ? "default" : "pointer",
//               letterSpacing: "0.04em",
//             }}
//           >
//             {isSubscribed
//               ? "✓  Subscribed"
//               : isFeatured
//               ? "Activate Plan →"
//               : "Unlock Benefits →"}
//           </button>

//           {/* Footer note */}
//           <div
//             className="flex items-center justify-center gap-2 mt-4 text-[11px] font-medium"
//             style={{ color: isFeatured ? "rgba(255,255,255,0.35)" : "#94a3b8" }}
//           >
//             <ShieldIcon featured={isFeatured} />
//             {isFeatured
//               ? "Trusted by 2,000+ retail partners worldwide"
//               : "Enterprise SLA · Dedicated success team"}
//           </div>

//         </div>
//       </div>
//     </motion.div>
//   );
// }


// export default function RetailerProductView() {
//   const [loading, setLoading] = useState(false);
//   const [products, setProducts] = useState([]);

//   const [isOrderOpen, setIsOrderOpen] = useState(false);
//   const [selectedProduct, setSelectedProduct] = useState(null);

//   const authUserString = localStorage.getItem("authUserData");
//   const authUser = authUserString ? JSON.parse(authUserString) : null;
//   const companyId = authUser?.company_id;
//   const retailerid = authUser?.id;

//    const location = useLocation();

//   const [activeTab, setActiveTab] = useState(
//     location.state?.activeTab || "subscription"
//   );
//   const [subscribedProducts, setSubscribedProducts] = useState([]);

//   const navigate = useNavigate()

//   const isRetailerMobile =
//   typeof window !== "undefined" &&
//   window.innerWidth < 640 &&
//   (authUser?.role === 5 || authUser?.role === 6);
 

//    const mostPopularId = products.length
//   ? products.reduce((max, p) =>
//       Number(p.sold_count) > Number(max.sold_count) ? p : max
//     ).id
//   : null;

//   useEffect(() => {
//   if (location.state?.activeTab) {
//     setActiveTab(location.state.activeTab);
//   }
// }, [location.state]);

//   useEffect(() => {
//     async function fetchProducts() {
//       if (!companyId) return;
//       setLoading(true);
//       try {
//         const res = await axios.get(`${BASE_URL}warranty/company-products`, {
//           params: { company_id: companyId , is_offer: 1, retailer_id: retailerid },
//         });
//         const companyBlock = res.data?.data?.[0];
//         const mapped =
//           companyBlock?.products?.map((cp) => ({
//             company_product_id: cp.company_product_id,
//             margin: cp.margin,

//             // ✅ ADD THIS
//             is_subscribed: cp.is_subscribed,

//             // ✅ OPTIONAL
//             subscription: cp.subscription,

//             ...cp.product,
//           })) || [];
//         setProducts(mapped);
//       } catch (e) {
//         console.error("Company products error", e);
//       } finally {
//         setLoading(false);
//       }
//     }
//     fetchProducts();
//   }, [companyId]);


//   if (loading) {
//     return (
//       <RegularLoader/>
//     );
//   }

//   if (!products.length) {
//     return (
//       <div className="flex items-center justify-center py-5 text-slate-400 text-sm">
//         No products available.
//       </div>
//     );
//   }

//   return (
//     /* py-16 gives breathing room for the featured card's translateY(-28px) to protrude into */
    
//     <div
//       className={`px-6 py-2 min-h-screen ${
//         isRetailerMobile
//           ? ""
//           : ""
//       }`}
//     >

//       <div className="flex gap-3 mb-6">
//         <button
//           onClick={() => setActiveTab("subscription")}
//           className={`px-4 py-1 rounded-xl text-sm font-semibold transition ${
//             activeTab === "subscription"
//               ? "bg-blue-600 text-white shadow"
//               : "bg-white text-slate-600 border"
//           }`}
//         >
//           Subscription
//         </button>

//         <button
//           onClick={() => setActiveTab("subscribed")}
//           className={`px-4 py-1 rounded-xl text-sm
//              font-semibold transition ${
//             activeTab === "subscribed"
//               ? "bg-blue-600 text-white shadow"
//               : "bg-white text-slate-600 border"
//           }`}
//         >
//           Subscribed Products
//         </button>
//       </div>
//      {/* 👇 CONDITIONAL RENDER */}
//     {activeTab === "subscription" && (
//       <div className="grid grid-cols-1 sm:grid-cols-4  gap-2">
//         {products.map((p, i) => (
//           <div key={p.company_product_id} className="w-full sm:w-70">
//             <ProductCard
//               product={p}
//               index={i}
//               isPopular={p.id === mostPopularId}
//               navigate={navigate}
//               products={products}
//             />
//           </div>
//         ))}
//       </div>
//     )}

//   {activeTab === "subscribed" && (
//   <SubscribedTab companyId={retailerid} />
// )}

//     </div>
//   );
// }


// import { useEffect, useState } from "react";
// import axios from "axios";
// import { BASE_URL } from "../../../../config/api";
// import { motion } from "framer-motion";
// import { useNavigate } from "react-router-dom";
// import { useLocation } from "react-router-dom";
// import SubscribedTab from "./SubscribedTab";
// import RegularLoader from "../../../../Component/Loader/RegularLoader";
// import toast from "react-hot-toast";

// function ProductCard({ products, product, index, onOrder, isPopular, navigate  }) {
//   const [showAllFeatures, setShowAllFeatures] = useState(false);
//   const [showAllCoverage, setShowAllCoverage] = useState(false);
//   const [showAllBenefits, setShowAllBenefits] = useState(false);

//   const FEATURE_LIMIT = 3;
//   const COVERAGE_LIMIT = 3;
//   const BENEFITS_LIMIT = 3;

//   const category = product.categories?.[0];

//   const features = product.features
//     ? product.features.split("✔").map((f) => f.trim()).filter(Boolean)
//     : [];

//   const coverage = product.coverage
//     ? product.coverage.split(".").map((c) => c.trim()).filter(Boolean)
//     : [];

//   const benefits = product.retailer_benifits
//     ? product.retailer_benifits.split(".").map((c) => c.trim()).filter(Boolean)
//     : [];


//   const isFeatured = isPopular;

//   const displayedFeatures = showAllFeatures ? features : features.slice(0, FEATURE_LIMIT);
//   const displayedCoverage = showAllCoverage ? coverage : coverage.slice(0, COVERAGE_LIMIT);
//   const displayedbenefits = showAllBenefits ? benefits : benefits.slice(0, BENEFITS_LIMIT);

//   const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");

// const isRetailerMobile =
//   typeof window !== "undefined" &&
//   window.innerWidth < 640 &&
//   (authUser?.role === 5 || authUser?.role === 6);

//   const handleSubscribe = async () => {
//   try {
//     const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");

//     const res = await axios.get(
//       `${BASE_URL}warranty/check-retailer-active-plan`,
//       {
//         params: {
//           retailer_id: authUser?.id,
//           package_id: product?.id,
//         },
//       }
//     );

//     const data = res.data;

//     // ✅ Already subscribed
//     if (data?.success && data?.has_active_plan) {
//       toast.error(
//         "You are already subscribed to this product. Please select a different product."
//       );
//       return;
//     }

//     // ✅ Not subscribed → go next
//     if (data?.success && !data?.has_active_plan) {
//       navigate("/ordersummery", {
//         state: { company_product_id: product.company_product_id },
//       });
//     }

//   } catch (err) {
//     console.error(err);
//     alert("Something went wrong");
//   }
// };

//   return (
//     <motion.div
//       initial={{ opacity: 0, y: 30 }}
//       animate={{ opacity: 1, y: 0 }}
//       transition={{ duration: 0.5, delay: index * 0.1, ease: "easeOut" }}
//       // ⚠️ NO overflow-hidden here — that's what was clipping the border-radius
//       style={{
//         position: "relative",
//         zIndex: isFeatured ? 10 : 1,
//         // Featured card shifts UP so it protrudes above siblings
//         transform: isFeatured ? "translateY(-28px)" : "translateY(0px)",
//         width: "100%",
//       }}
//     >
//       {/* This inner div carries the visual card — border-radius + overflow-hidden here is safe
//           because it's not the element being transformed */}
//       <div
//         style={{
//           borderRadius: "20px",
//           overflow: "hidden",
//           display: "flex",
//           flexDirection: "column",
//           height: "100%",
//         }}
//         className={`transition-all duration-300
//           ${
//             isRetailerMobile
//               ? isFeatured
//                 ? "bg-gradient-to-br from-[#3b82f6] to-[#2563eb] text-white shadow-xl"
//                 : "bg-white/70 backdrop-blur-md border border-white/60 shadow-[0_10px_30px_rgba(0,0,0,0.08)] text-slate-800"
//               : isFeatured
//               ? "bg-[#4a5bf3] text-white shadow-2xl"
//               : "bg-white text-slate-800 shadow-lg border border-slate-200"
//           }`}
//       >
//         {/* Badge row */}
//         <div
//           className="flex justify-center"
//           style={{
//             paddingTop: isFeatured ? "32px" : "28px",
//             paddingBottom: "8px",
//             minHeight: isFeatured ? "72px" : "52px",
//           }}
//         >
//           {isFeatured && (
//             <span className="bg-yellow-400 text-yellow-900 text-xs font-extrabold px-5 py-1.5 rounded-full uppercase tracking-widest whitespace-nowrap">
//               Most Popular
//             </span>
//           )}
//         </div>

//         {/* Title */}
//         <div className="px-6 pb-2 text-center">
//           <h3
//             style={{ fontSize: isFeatured ? "20px" : "17px" }}
//             className={`font-extrabold uppercase tracking-widest
//               ${
//                 isRetailerMobile
//                   ? isFeatured
//                     ? "text-white"
//                     : "text-slate-800"
//                   : isFeatured
//                   ? "text-white"
//                   : "text-slate-800"
//               }`}
//           >
//             { product.name}
//           </h3>
//           {/* <p className={`text-[11px] mt-1 font-medium line-clamp-2
//             ${isFeatured ? "text-white/70" : "text-slate-400"}`}>
//             {product.name}
//           </p> */}
//         </div>

//         {/* Price Shape */}
//         <div className="flex justify-center" style={{ margin: "10px 0" }}>
//           <div
//             className="flex flex-col items-center justify-center shadow-md"
//             style={{
//               width: isFeatured ? "100px" : "84px",
//               height: isFeatured ? "100px" : "84px",
//               background: "#f1f5f9",
//               borderRadius: isFeatured
//                 ? "44% 56% 52% 48% / 46% 44% 56% 54%"
//                 : "50%",
//             }}
//           >
//             <span
//               style={{ fontSize: isFeatured ? "20px" : "18px", color: "#1e293b" }}
//               className="font-extrabold leading-tight"
//             >
//               {product.is_percent === 1 ? `${product.mrp}%` : `₹${product.mrp}`}
//             </span>
//             <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-400 mt-0.5">
//               Per Device
//             </span>
//           </div>
//         </div>

//         {/* Meta info */}
//         <div className={`px-6 text-center leading-relaxed
//           ${isFeatured ? "text-white/85" : "text-slate-500"}`}>
//           <p className={`text-sm font-bold ${
//             isRetailerMobile
//               ? isFeatured
//                 ? "text-white/90"
//                 : "text-blue-600"
//               : isFeatured
//               ? "text-white"
//               : "text-blue-600"
//           }`}>
//             Valid For {product.sub_val_days} Days <br/>
//             Enroll Upto {product.enroll_max} Devices
//           </p>
//           {/* {product.min_value && (
//             <p className="text-xs mt-1">
//               ₹{product.min_value?.toLocaleString("en-IN")} – ₹{product.max_value?.toLocaleString("en-IN")}
//             </p>
//           )} */}
        
//         </div>

//         {/* Features */}
//         {/* {features.length > 0 && (
//           <div className="px-6 mt-4">
//             <p className={`text-[10px] font-bold uppercase tracking-widest mb-1.5
//               ${isFeatured ? "text-white/60" : "text-slate-400"}`}>
//               benefits
//             </p>
//             <ul className="space-y-1">
//               {displayedbenefits.map((f, i) => (
//                 <li key={i} className={`flex items-start gap-1.5 text-[11px]
//                   ${isFeatured ? "text-white/80" : "text-slate-600"}`}>
//                   <span className={`flex-shrink-0 font-bold mt-0.5
//                     ${isFeatured ? "text-yellow-300" : "text-blue-500"}`}>✔</span>
//                   {f}
//                 </li>
//               ))}
//             </ul>
//             {benefits.length > BENEFITS_LIMIT && (
//               <button
//                 onClick={() => setShowAllBenefits(!showAllBenefits)}
//                 className={`text-[10px] font-bold mt-1.5
//                   ${isFeatured ? "text-yellow-300" : "text-blue-600"}`}>
//                 {showAllBenefits ? "View Less ▲" : `+${benefits.length - BENEFITS_LIMIT} more ▼`}
//               </button>
//             )}
//           </div>
//         )} */}
//         <div className="flex-1" />

//         {/* CTA Button */}
//         <div style={{ padding: isFeatured ? "20px 24px 36px" : "20px 24px 28px" }}>
//           <button
//             onClick={() => {
//                 if (product?.is_subscribed === true) {
//                   return;
//                 }

//                 handleSubscribe();
//               }}
//             className={`w-full py-2 rounded-2xl font-extrabold text-sm uppercase tracking-widest
//               transition-all duration-200 active:scale-95

//               ${
//                 product?.is_subscribed

//                   // ✅ SUBSCRIBED STYLE
//                   ? `
//                     bg-white
//                     text-emerald-500
//                     shadow-lg
//                     cursor-default
//                   `

//                   // ✅ NORMAL STYLE
//                   : isRetailerMobile
//                   ? isFeatured
//                     ? "bg-white text-blue-700 shadow-lg"
//                     : "bg-blue-600 text-white shadow-md"
//                   : isFeatured
//                   ? "bg-white text-[#2233CC] hover:bg-blue-50 shadow-lg"
//                   : "bg-[#2233CC] text-white hover:bg-blue-800 shadow"
//               }
//             `}
//           >
//             {Boolean(product?.is_subscribed)
//             ? "Subscribed"
//             : "Subscribe Now"}
//           </button>
//         </div>
//       </div>
//     </motion.div>
//   );
// }

// export default function RetailerProductView() {
//   const [loading, setLoading] = useState(false);
//   const [products, setProducts] = useState([]);

//   const [isOrderOpen, setIsOrderOpen] = useState(false);
//   const [selectedProduct, setSelectedProduct] = useState(null);

//   const authUserString = localStorage.getItem("authUserData");
//   const authUser = authUserString ? JSON.parse(authUserString) : null;
//   const companyId = authUser?.company_id;
//   const retailerid = authUser?.id;

//    const location = useLocation();

//   const [activeTab, setActiveTab] = useState(
//     location.state?.activeTab || "subscription"
//   );
//   const [subscribedProducts, setSubscribedProducts] = useState([]);

//   const navigate = useNavigate()

//   const isRetailerMobile =
//   typeof window !== "undefined" &&
//   window.innerWidth < 640 &&
//   (authUser?.role === 5 || authUser?.role === 6);
 

//    const mostPopularId = products.length
//   ? products.reduce((max, p) =>
//       Number(p.sold_count) > Number(max.sold_count) ? p : max
//     ).id
//   : null;

//   useEffect(() => {
//   if (location.state?.activeTab) {
//     setActiveTab(location.state.activeTab);
//   }
// }, [location.state]);

//   useEffect(() => {
//     async function fetchProducts() {
//       if (!companyId) return;
//       setLoading(true);
//       try {
//         const res = await axios.get(`${BASE_URL}warranty/company-products`, {
//           params: { company_id: companyId , is_offer: 1, retailer_id: retailerid },
//         });
//         const companyBlock = res.data?.data?.[0];
//         const mapped =
//           companyBlock?.products?.map((cp) => ({
//             company_product_id: cp.company_product_id,
//             margin: cp.margin,

//             // ✅ ADD THIS
//             is_subscribed: cp.is_subscribed,

//             // ✅ OPTIONAL
//             subscription: cp.subscription,

//             ...cp.product,
//           })) || [];
//         setProducts(mapped);
//       } catch (e) {
//         console.error("Company products error", e);
//       } finally {
//         setLoading(false);
//       }
//     }
//     fetchProducts();
//   }, [companyId]);


//   if (loading) {
//     return (
//       <RegularLoader/>
//     );
//   }

//   if (!products.length) {
//     return (
//       <div className="flex items-center justify-center py-5 text-slate-400 text-sm">
//         No products available.
//       </div>
//     );
//   }

//   return (
//     /* py-16 gives breathing room for the featured card's translateY(-28px) to protrude into */
    
//     <div
//       className={`px-6 py-2 min-h-screen ${
//         isRetailerMobile
//           ? ""
//           : ""
//       }`}
//     >

//       <div className="flex gap-3 mb-6">
//         <button
//           onClick={() => setActiveTab("subscription")}
//           className={`px-4 py-1 rounded-xl text-sm font-semibold transition ${
//             activeTab === "subscription"
//               ? "bg-blue-600 text-white shadow"
//               : "bg-white text-slate-600 border"
//           }`}
//         >
//           Subscription
//         </button>

//         <button
//           onClick={() => setActiveTab("subscribed")}
//           className={`px-4 py-1 rounded-xl text-sm
//              font-semibold transition ${
//             activeTab === "subscribed"
//               ? "bg-blue-600 text-white shadow"
//               : "bg-white text-slate-600 border"
//           }`}
//         >
//           Subscribed Products
//         </button>
//       </div>
//      {/* 👇 CONDITIONAL RENDER */}
//     {activeTab === "subscription" && (
//       <div className="flex flex-wrap gap-5">
//         {products.map((p, i) => (
//           <div key={p.company_product_id} className="w-full sm:w-60">
//             <ProductCard
//               product={p}
//               index={i}
//               isPopular={p.id === mostPopularId}
//               navigate={navigate}
//               products={products}
//             />
//           </div>
//         ))}
//       </div>
//     )}

//   {activeTab === "subscribed" && (
//   <SubscribedTab companyId={retailerid} />
// )}

//     </div>
//   );
// }


// import { useEffect, useState } from "react";
// import axios from "axios";
// import { BASE_URL } from "../../../../config/api";
// import { motion } from "framer-motion";
// import { useNavigate } from "react-router-dom";
// import { useLocation } from "react-router-dom";
// import SubscribedTab from "./SubscribedTab";
// import RegularLoader from "../../../../Component/Loader/RegularLoader";
// import toast from "react-hot-toast";

// function ProductCard({ products, product, index, onOrder, isPopular, navigate  }) {
//   const [showAllFeatures, setShowAllFeatures] = useState(false);
//   const [showAllCoverage, setShowAllCoverage] = useState(false);
//   const [showAllBenefits, setShowAllBenefits] = useState(false);

//   const FEATURE_LIMIT = 3;
//   const COVERAGE_LIMIT = 3;
//   const BENEFITS_LIMIT = 3;

//   const category = product.categories?.[0];

//   const features = product.features
//     ? product.features.split("✔").map((f) => f.trim()).filter(Boolean)
//     : [];

//   const coverage = product.coverage
//     ? product.coverage.split(".").map((c) => c.trim()).filter(Boolean)
//     : [];

//   const benefits = product.retailer_benifits
//     ? product.retailer_benifits.split(".").map((c) => c.trim()).filter(Boolean)
//     : [];


//   const isFeatured = isPopular;

//   const displayedFeatures = showAllFeatures ? features : features.slice(0, FEATURE_LIMIT);
//   const displayedCoverage = showAllCoverage ? coverage : coverage.slice(0, COVERAGE_LIMIT);
//   const displayedbenefits = showAllBenefits ? benefits : benefits.slice(0, BENEFITS_LIMIT);

//   const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");

// const isRetailerMobile =
//   typeof window !== "undefined" &&
//   window.innerWidth < 640 &&
//   (authUser?.role === 5 || authUser?.role === 6);

//   const handleSubscribe = async () => {
//   try {
//     const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");

//     const res = await axios.get(
//       `${BASE_URL}warranty/check-retailer-active-plan`,
//       {
//         params: {
//           retailer_id: authUser?.id,
//           package_id: product?.id,
//         },
//       }
//     );

//     const data = res.data;

//     // ✅ Already subscribed
//     if (data?.success && data?.has_active_plan) {
//       toast.error(
//         "You are already subscribed to this product. Please select a different product."
//       );
//       return;
//     }

//     // ✅ Not subscribed → go next
//     if (data?.success && !data?.has_active_plan) {
//       navigate("/ordersummery", {
//         state: { company_product_id: product.company_product_id },
//       });
//     }

//   } catch (err) {
//     console.error(err);
//     alert("Something went wrong");
//   }
// };

//   return (
//     <motion.div
//       initial={{ opacity: 0, y: 30 }}
//       animate={{ opacity: 1, y: 0 }}
//       transition={{ duration: 0.5, delay: index * 0.1, ease: "easeOut" }}
//       // ⚠️ NO overflow-hidden here — that's what was clipping the border-radius
//       style={{
//         position: "relative",
//         zIndex: isFeatured ? 10 : 1,
//         // Featured card shifts UP so it protrudes above siblings
//         transform: isFeatured ? "translateY(-28px)" : "translateY(0px)",
//         width: "100%",
//       }}
//     >
//       {/* This inner div carries the visual card — border-radius + overflow-hidden here is safe
//           because it's not the element being transformed */}
//       <div
//         style={{
//           borderRadius: "20px",
//           overflow: "hidden",
//           display: "flex",
//           flexDirection: "column",
//           height: "100%",
//         }}
//         className={`transition-all duration-300
//           ${
//             isRetailerMobile
//               ? isFeatured
//                 ? "bg-gradient-to-br from-[#3b82f6] to-[#2563eb] text-white shadow-xl"
//                 : "bg-white/70 backdrop-blur-md border border-white/60 shadow-[0_10px_30px_rgba(0,0,0,0.08)] text-slate-800"
//               : isFeatured
//               ? "bg-[#4a5bf3] text-white shadow-2xl"
//               : "bg-white text-slate-800 shadow-lg border border-slate-200"
//           }`}
//       >
//         {/* Badge row */}
//         <div
//           className="flex justify-center"
//           style={{
//             paddingTop: isFeatured ? "32px" : "28px",
//             paddingBottom: "8px",
//             minHeight: isFeatured ? "72px" : "52px",
//           }}
//         >
//           {isFeatured && (
//             <span className="bg-yellow-400 text-yellow-900 text-xs font-extrabold px-5 py-1.5 rounded-full uppercase tracking-widest whitespace-nowrap">
//               Most Popular
//             </span>
//           )}
//         </div>

//         {/* Title */}
//         <div className="px-6 pb-2 text-center">
//           <h3
//             style={{ fontSize: isFeatured ? "20px" : "17px" }}
//             className={`font-extrabold uppercase tracking-widest
//               ${
//                 isRetailerMobile
//                   ? isFeatured
//                     ? "text-white"
//                     : "text-slate-800"
//                   : isFeatured
//                   ? "text-white"
//                   : "text-slate-800"
//               }`}
//           >
//             { product.name}
//           </h3>
//           {/* <p className={`text-[11px] mt-1 font-medium line-clamp-2
//             ${isFeatured ? "text-white/70" : "text-slate-400"}`}>
//             {product.name}
//           </p> */}
//         </div>

//         {/* Price Shape */}
//         <div className="flex justify-center" style={{ margin: "10px 0" }}>
//           <div
//             className="flex flex-col items-center justify-center shadow-md"
//             style={{
//               width: isFeatured ? "100px" : "84px",
//               height: isFeatured ? "100px" : "84px",
//               background: "#f1f5f9",
//               borderRadius: isFeatured
//                 ? "44% 56% 52% 48% / 46% 44% 56% 54%"
//                 : "50%",
//             }}
//           >
//             <span
//               style={{ fontSize: isFeatured ? "20px" : "18px", color: "#1e293b" }}
//               className="font-extrabold leading-tight"
//             >
//               {product.is_percent === 1 ? `${product.mrp}%` : `₹${product.mrp}`}
//             </span>
//             <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-400 mt-0.5">
//               Per Plan
//             </span>
//           </div>
//         </div>

//         {/* Meta info */}
//         <div className={`px-6 text-center leading-relaxed
//           ${isFeatured ? "text-white/85" : "text-slate-500"}`}>
//           <p className={`text-sm font-bold ${
//             isRetailerMobile
//               ? isFeatured
//                 ? "text-white/90"
//                 : "text-blue-600"
//               : isFeatured
//               ? "text-white"
//               : "text-blue-600"
//           }`}>
//             {product.sub_val_days} Days &nbsp;·&nbsp; {product.enroll_max} Devices
//           </p>
//           {/* {product.min_value && (
//             <p className="text-xs mt-1">
//               ₹{product.min_value?.toLocaleString("en-IN")} – ₹{product.max_value?.toLocaleString("en-IN")}
//             </p>
//           )} */}
        
//         </div>

//         {/* Features */}
//         {features.length > 0 && (
//           <div className="px-6 mt-4">
//             <p className={`text-[10px] font-bold uppercase tracking-widest mb-1.5
//               ${isFeatured ? "text-white/60" : "text-slate-400"}`}>
//               benefits
//             </p>
//             <ul className="space-y-1">
//               {displayedbenefits.map((f, i) => (
//                 <li key={i} className={`flex items-start gap-1.5 text-[11px]
//                   ${isFeatured ? "text-white/80" : "text-slate-600"}`}>
//                   <span className={`flex-shrink-0 font-bold mt-0.5
//                     ${isFeatured ? "text-yellow-300" : "text-blue-500"}`}>✔</span>
//                   {f}
//                 </li>
//               ))}
//             </ul>
//             {benefits.length > BENEFITS_LIMIT && (
//               <button
//                 onClick={() => setShowAllBenefits(!showAllBenefits)}
//                 className={`text-[10px] font-bold mt-1.5
//                   ${isFeatured ? "text-yellow-300" : "text-blue-600"}`}>
//                 {showAllBenefits ? "View Less ▲" : `+${benefits.length - BENEFITS_LIMIT} more ▼`}
//               </button>
//             )}
//           </div>
//         )}
//         <div className="flex-1" />

//         {/* CTA Button */}
//         <div style={{ padding: isFeatured ? "20px 24px 36px" : "20px 24px 28px" }}>
//           <button
//             onClick={() => {
//                 if (product?.is_subscribed === true) {
//                   return;
//                 }

//                 handleSubscribe();
//               }}
//             className={`w-full py-2 rounded-2xl font-extrabold text-sm uppercase tracking-widest
//               transition-all duration-200 active:scale-95

//               ${
//                 product?.is_subscribed

//                   // ✅ SUBSCRIBED STYLE
//                   ? `
//                     bg-white
//                     text-emerald-500
//                     shadow-lg
//                     cursor-default
//                   `

//                   // ✅ NORMAL STYLE
//                   : isRetailerMobile
//                   ? isFeatured
//                     ? "bg-white text-blue-700 shadow-lg"
//                     : "bg-blue-600 text-white shadow-md"
//                   : isFeatured
//                   ? "bg-white text-[#2233CC] hover:bg-blue-50 shadow-lg"
//                   : "bg-[#2233CC] text-white hover:bg-blue-800 shadow"
//               }
//             `}
//           >
//             {Boolean(product?.is_subscribed)
//             ? "Subscribed"
//             : "Subscribe Now"}
//           </button>
//         </div>
//       </div>
//     </motion.div>
//   );
// }

// export default function RetailerProductView() {
//   const [loading, setLoading] = useState(false);
//   const [products, setProducts] = useState([]);

//   const [isOrderOpen, setIsOrderOpen] = useState(false);
//   const [selectedProduct, setSelectedProduct] = useState(null);

//   const authUserString = localStorage.getItem("authUserData");
//   const authUser = authUserString ? JSON.parse(authUserString) : null;
//   const companyId = authUser?.company_id;
//   const retailerid = authUser?.id;

//    const location = useLocation();

//   const [activeTab, setActiveTab] = useState(
//     location.state?.activeTab || "subscription"
//   );
//   const [subscribedProducts, setSubscribedProducts] = useState([]);

//   const navigate = useNavigate()

//   const isRetailerMobile =
//   typeof window !== "undefined" &&
//   window.innerWidth < 640 &&
//   (authUser?.role === 5 || authUser?.role === 6);
 

//    const mostPopularId = products.length
//   ? products.reduce((max, p) =>
//       Number(p.sold_count) > Number(max.sold_count) ? p : max
//     ).id
//   : null;

//   useEffect(() => {
//   if (location.state?.activeTab) {
//     setActiveTab(location.state.activeTab);
//   }
// }, [location.state]);

//   useEffect(() => {
//     async function fetchProducts() {
//       if (!companyId) return;
//       setLoading(true);
//       try {
//         const res = await axios.get(`${BASE_URL}warranty/company-products`, {
//           params: { company_id: companyId , is_offer: 1, retailer_id: retailerid },
//         });
//         const companyBlock = res.data?.data?.[0];
//         const mapped =
//           companyBlock?.products?.map((cp) => ({
//             company_product_id: cp.company_product_id,
//             margin: cp.margin,

//             // ✅ ADD THIS
//             is_subscribed: cp.is_subscribed,

//             // ✅ OPTIONAL
//             subscription: cp.subscription,

//             ...cp.product,
//           })) || [];
//         setProducts(mapped);
//       } catch (e) {
//         console.error("Company products error", e);
//       } finally {
//         setLoading(false);
//       }
//     }
//     fetchProducts();
//   }, [companyId]);


//   if (loading) {
//     return (
//       <RegularLoader/>
//     );
//   }

//   if (!products.length) {
//     return (
//       <div className="flex items-center justify-center py-5 text-slate-400 text-sm">
//         No products available.
//       </div>
//     );
//   }

//   return (
//     /* py-16 gives breathing room for the featured card's translateY(-28px) to protrude into */
    
//     <div
//       className={`px-6 py-2 min-h-screen ${
//         isRetailerMobile
//           ? ""
//           : ""
//       }`}
//     >

//       <div className="flex gap-3 mb-6">
//         <button
//           onClick={() => setActiveTab("subscription")}
//           className={`px-4 py-1 rounded-xl text-sm font-semibold transition ${
//             activeTab === "subscription"
//               ? "bg-blue-600 text-white shadow"
//               : "bg-white text-slate-600 border"
//           }`}
//         >
//           Subscription
//         </button>

//         <button
//           onClick={() => setActiveTab("subscribed")}
//           className={`px-4 py-1 rounded-xl text-sm
//              font-semibold transition ${
//             activeTab === "subscribed"
//               ? "bg-blue-600 text-white shadow"
//               : "bg-white text-slate-600 border"
//           }`}
//         >
//           Subscribed Products
//         </button>
//       </div>
//      {/* 👇 CONDITIONAL RENDER */}
//     {activeTab === "subscription" && (
//       <div className="flex flex-wrap gap-5">
//         {products.map((p, i) => (
//           <div key={p.company_product_id} className="w-full sm:w-60">
//             <ProductCard
//               product={p}
//               index={i}
//               isPopular={p.id === mostPopularId}
//               navigate={navigate}
//               products={products}
//             />
//           </div>
//         ))}
//       </div>
//     )}

//   {activeTab === "subscribed" && (
//   <SubscribedTab companyId={retailerid} />
// )}

//     </div>
//   );
// }


// *************************************************************original code without subscription********************************************************************

// import { useEffect, useState } from "react";
// import axios from "axios";
// import { BASE_URL } from "../../../../config/api";
// import { useContext } from "react";
// import { AuthContext } from "../../../../context/AuthContext";
// import { motion } from "framer-motion";


// function ProductCard({ product, isRetailerMobile }) {

//   const [showAllFeatures, setShowAllFeatures] = useState(false);
//   const [showAllCoverage, setShowAllCoverage] = useState(false);

//   const FEATURE_LIMIT = 2;
//   const COVERAGE_LIMIT = 2;
//   const category = product.categories?.[0];

//   const features = product.features
//   ? product.features.split(".").map((f) => f.trim()).filter(Boolean)
//   : [];

//   const coverage = product.coverage
//     ? product.coverage.split(".").map((c) => c.trim()).filter(Boolean)
//     : [];

//   const exclusions = product.exclusions
//     ? product.exclusions.split(".").map((e) => e.trim()).filter(Boolean)
//     : [];

//   const getTheme = () => {
//     const name = category?.name?.toLowerCase() || "";
//     if (name.includes("iphone")) return purpleTheme;
//     if (name.includes("android")) return blueTheme;
//     return emeraldTheme;
//   };

//   const blueTheme = {
//     gradient: "from-blue-500 to-blue-700",
//     accent: "border-blue-400",
//     check: "text-blue-600",
//     badge: "bg-blue-500",
//   };

//   const purpleTheme = {
//     gradient: "from-purple-500 to-purple-700",
//     accent: "border-purple-400",
//     check: "text-purple-600",
//     badge: "bg-purple-500",
//   };

//   const emeraldTheme = {
//     gradient: "from-emerald-500 to-emerald-700",
//     accent: "border-emerald-400",
//     check: "text-emerald-600",
//     badge: "bg-emerald-500",
//   };

//   const theme = getTheme();

//   return (

//     <motion.div
//   initial={{ opacity: 0, y: 30 }}
//   animate={{ opacity: 1, y: 0 }}
//   transition={{ duration: 0.6, ease: "easeOut" }}
//   className="..."
// >
//     <div
//       className={`relative w-full max-w-xs mx-auto rounded-2xl overflow-hidden transition-all duration-500 ${
//         isRetailerMobile
//           ? "glass-card bg-white/10 backdrop-blur-2xl border border-white/30 shadow-[0_20px_50px_rgba(31,38,135,0.25)] hover:shadow-[0_25px_70px_rgba(59,130,246,0.35)] hover:-translate-y-1"
//           : "bg-white shadow-xl"
//       }`}
//     >

//       {/* 🔵 Header */}
//         <div
//           className={`relative min-h-[170px] bg-gradient-to-br ${theme.gradient} bg-[length:200%_200%] animate-gradientMove`}
//           style={{
//             clipPath: "polygon(0 0, 100% 0, 100% 70%, 0 100%)",
//           }}
//         >
//           <div className="p-2 text-white flex flex-col h-full">

//             {/* Top Row → Title + Claims */}
//             <div className="flex justify-between items-start gap-2">
              
//               <div className="text-sm font-bold uppercase leading-tight break-words line-clamp-2 flex-1">
//                 {product.name}
//               </div>

//               <div
//                 className={`text-white text-sm px-2 py-1 rounded-md font-semibold whitespace-nowrap ${
//                   isRetailerMobile
//                     ? `${theme.badge} shadow-lg`
//                     : "bg-emerald-500"
//                 }`}
//               >
//                 {product.claims} Claims
//               </div>
//             </div>

//             {/* Category */}
//             <div className="text-sm opacity-80 mt-2 mb-2">
//               {category?.name}
//             </div>

//             {/* Validity */}
//             <div className="mt-auto text-">
//               <span className="bg-white/20 px-2 py-1 rounded-md backdrop-blur-sm font-semibold text-xs">
//                 {product.validity} Days Validity
//               </span>
//             </div>

//           </div>
//         </div>


//       {/* ⚫ Price Circle */}
//       <div className="absolute top-20 left-1/2 -translate-x-1/2 z-10">
//         {isRetailerMobile && (
//           <div className="absolute inset-0 w-24 h-24 -translate-x-1 -translate-y-1 rounded-full bg-blue-500 blur-2xl opacity-40 animate-pulse"></div>
//         )}
//         <div
//           className={`w-20 h-20 rounded-full bg-slate-900 text-white flex flex-col items-center justify-center shadow-lg border-[4px] border-dashed ${theme.accent}`}
//         >
//           <div className="text-lg font-extrabold">
//             {product.is_percent === 1
//               ? `${product.mrp}%`
//               : `₹${product.mrp}`}
//           </div>
//           <div className="text-[9px] uppercase opacity-80">
//             {product.is_percent === 1 ? "Rate" : "MRP"}
//           </div>
//         </div>
//       </div>

//       {/* ⚪ Body */}
//       <div
//         className={`pt-5 px-4 pb-6 space-y-3 text-sm ${
//           isRetailerMobile ? "text-white" : ""
//         }`}
//       >

//         {/* 💰 Value Range + Profit */}
//         <div className="grid grid-cols-2 gap-2">
//           <Info
//             label="Value Range"
//             value={`₹${product.min_value} - ₹${product.max_value}`}
//             isRetailerMobile={isRetailerMobile}
//           />
//           <Info
//             label="Profit"
//             value={
//               product.is_percent === 1
//                 ? `${product.margin}%`
//                 : `₹${product.margin}`
//             }
//             isRetailerMobile={isRetailerMobile}
//           />
//         </div>

//         {/* Features */}
//         {features.length > 0 && (
//           <Section title="Features" isRetailerMobile={isRetailerMobile}>
//             {(showAllFeatures
//               ? features
//               : features.slice(0, FEATURE_LIMIT)
//             ).map((f, i) => (
//               <li key={i} className="flex gap-1">
//                 <span>{f}</span>
//               </li>
//             ))}

//             {features.length > FEATURE_LIMIT && (
//               <button
//                 onClick={() => setShowAllFeatures(!showAllFeatures)}
//                 className="text-sm mt-1 text-blue-600 font-semibold"
//               >
//                 {showAllFeatures ? "View Less" : "View More"}
//               </button>
//             )}
//           </Section>
//         )}

//         {/* Coverage */}
//         {coverage.length > 0 && (
//           <Section title="Coverage" isRetailerMobile={isRetailerMobile}>
//             {(showAllCoverage
//               ? coverage
//               : coverage.slice(0, COVERAGE_LIMIT)
//             ).map((c, i) => (
//               <li key={i} className="flex gap-1">
//                 <span className={`${theme.check} font-bold`}>✓</span>
//                 <span>{c}</span>
//               </li>
//             ))}

//             {coverage.length > COVERAGE_LIMIT && (
//               <button
//                 onClick={() => setShowAllCoverage(!showAllCoverage)}
//                 className="text-sm mt-1 text-blue-600 font-semibold"
//               >
//                 {showAllCoverage ? "View Less" : "View More"}
//               </button>
//             )}
//           </Section>
//         )}

        

//         {/* Exclusions */}
//         {/* {exclusions.length > 0 && (
//           <Section title="Exclusions">
//             {exclusions.map((e, i) => (
//               <li key={i} className="flex gap-1 text-red-600">
//                 <span>✕</span>
//                 <span>{e}</span>
//               </li>
//             ))}
//           </Section>
//         )} */}
//       </div>

//       {/* Bottom Angle */}
//       <div
//         className={`absolute bottom-0 right-0 w-20 h-14 bg-gradient-to-tr ${theme.gradient}`}
//         style={{
//           clipPath: "polygon(30% 0, 100% 0, 100% 100%, 0 100%)",
//         }}
//       />
//     </div>

//     </motion.div>
//   );
// }

// /* Compact Info Box */
// function Info({ label, value, isRetailerMobile }) {
//   return (
//     <div
//       className={` rounded-md p-1.5 ${
//         isRetailerMobile
//           ? "glass-small-card bg-white/10 border border-white/20 text-black"
//           : "bg-slate-50 border border-slate-200"
//       }`}
//     >
//       <div className={`text-[9px] font-semibold ${
//         isRetailerMobile ? "text-black/70" : "text-slate-500"
//       }`}>
//         {label}
//       </div>

//       <div className={`text-sm font-semibold ${
//         isRetailerMobile ? "text-black" : "text-slate-900"
//       }`}>
//         {value}
//       </div>
//     </div>
//   );
// }

// /* Section */
// function Section({ title, children, isRetailerMobile }) {
//   return (
//     <div>
//       <div
//         className={`text-sm font-bold mb-1 ${
//           isRetailerMobile ? "text-black/80" : "text-slate-700"
//         }`}
//       >
//         {title}
//       </div>

//       <ul
//         className={`space-y-0.5 text-[11px] ${
//           isRetailerMobile ? "text-black/70" : "text-slate-600"
//         }`}
//       >
//         {children}
//       </ul>
//     </div>
//   );
// }

// export default function RetailerProductView() {
//   const [loading, setLoading] = useState(false);
//   const [products, setProducts] = useState([]);

//   const authUserString = localStorage.getItem("authUserData");
//   const authUser = authUserString ? JSON.parse(authUserString) : null;
//   const companyId = authUser?.company_id;

//   const { user } = useContext(AuthContext);

// const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

// useEffect(() => {
//   const handleResize = () => {
//     setIsMobile(window.innerWidth < 640);
//   };

//   window.addEventListener("resize", handleResize);
//   return () => window.removeEventListener("resize", handleResize);
// }, []);

// const isRetailerMobile =
//   Number(user?.role) === 5 && isMobile;

  

//   useEffect(() => {
//     async function fetchProducts() {
//       if (!companyId) return;

//       setLoading(true);
//       try {
//         const res = await axios.get(
//           `${BASE_URL}warranty/company-products`,
//           { params: { company_id: companyId } }
//         );

//         const companyBlock = res.data?.data?.[0];
//         const mapped =
//           companyBlock?.products?.map((cp) => ({
//             company_product_id: cp.company_product_id,
//             margin: cp.margin,
//             ...cp.product,
//           })) || [];

//         setProducts(mapped);
//       } catch (e) {
//         console.error("Company products error", e);
//       } finally {
//         setLoading(false);
//       }
//     }

//     fetchProducts();
//   }, [companyId]);

//   if (loading) {
//     return (
//       <div className="flex items-center justify-center py-10 text-sm text-slate-600">
//         Loading products…
//       </div>
//     );
//   }

//   return (
//     <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
//       {products.map((p) => (
//         <ProductCard
//           key={p.company_product_id}
//           product={p}
//           isRetailerMobile={isRetailerMobile}
//         />
//       ))}
//     </div>
//   );
// }


